// ═══════════════════════════════════════════════════════════════════════════
// SIMULATEUR CULTURAL V3 - Timeline avec Vecteurs Météo & Sol
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/admin/components/SimulateurCulturalV3.jsx
// Farm-Booking - Papy 73+ - Joshua Edition 🎮
// "The only winning move is to PREDICT"
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Sprout, Leaf, AlertTriangle, CheckCircle, Clock,
  ArrowRight, Play, Pause, RotateCcw, ChevronLeft, ChevronRight, Info,
  Thermometer, Droplets, Cloud, Wind, Sun, CloudRain, Snowflake,
  Activity, TrendingUp, TrendingDown, AlertCircle
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// DONNÉES MÉTÉO NORMALES NORMANDIE/OISE
// ═══════════════════════════════════════════════════════════════════════════

const NORMALES_METEO = {
  1:  { temp_min: 1.5, temp_max: 6.5, temp_moy: 4.0, temp_sol: 3.0, precip: 65, humidite: 88, ensoleillement: 55, vent: 18, jours_gel: 10, risque_gel: 'eleve', etp: 15 },
  2:  { temp_min: 1.5, temp_max: 7.5, temp_moy: 4.5, temp_sol: 3.5, precip: 55, humidite: 85, ensoleillement: 75, vent: 17, jours_gel: 8, risque_gel: 'eleve', etp: 25 },
  3:  { temp_min: 3.5, temp_max: 11.0, temp_moy: 7.0, temp_sol: 6.0, precip: 55, humidite: 80, ensoleillement: 130, vent: 16, jours_gel: 4, risque_gel: 'moyen', etp: 45 },
  4:  { temp_min: 5.5, temp_max: 14.0, temp_moy: 10.0, temp_sol: 9.0, precip: 50, humidite: 75, ensoleillement: 175, vent: 14, jours_gel: 1, risque_gel: 'faible', etp: 70 },
  5:  { temp_min: 9.0, temp_max: 18.0, temp_moy: 13.5, temp_sol: 13.0, precip: 60, humidite: 72, ensoleillement: 210, vent: 13, jours_gel: 0, risque_gel: 'nul', etp: 100 },
  6:  { temp_min: 12.0, temp_max: 21.0, temp_moy: 16.5, temp_sol: 17.0, precip: 55, humidite: 70, ensoleillement: 230, vent: 12, jours_gel: 0, risque_gel: 'nul', etp: 120 },
  7:  { temp_min: 14.0, temp_max: 23.5, temp_moy: 18.5, temp_sol: 19.0, precip: 55, humidite: 70, ensoleillement: 240, vent: 11, jours_gel: 0, risque_gel: 'nul', etp: 130 },
  8:  { temp_min: 13.5, temp_max: 23.5, temp_moy: 18.5, temp_sol: 19.0, precip: 50, humidite: 72, ensoleillement: 220, vent: 11, jours_gel: 0, risque_gel: 'nul', etp: 110 },
  9:  { temp_min: 11.0, temp_max: 20.0, temp_moy: 15.5, temp_sol: 16.0, precip: 55, humidite: 78, ensoleillement: 170, vent: 12, jours_gel: 0, risque_gel: 'nul', etp: 70 },
  10: { temp_min: 8.0, temp_max: 15.0, temp_moy: 11.5, temp_sol: 11.0, precip: 70, humidite: 85, ensoleillement: 110, vent: 14, jours_gel: 0, risque_gel: 'faible', etp: 40 },
  11: { temp_min: 4.5, temp_max: 10.0, temp_moy: 7.0, temp_sol: 7.0, precip: 75, humidite: 88, ensoleillement: 65, vent: 16, jours_gel: 3, risque_gel: 'moyen', etp: 20 },
  12: { temp_min: 2.5, temp_max: 7.0, temp_moy: 4.5, temp_sol: 4.0, precip: 80, humidite: 89, ensoleillement: 45, vent: 17, jours_gel: 7, risque_gel: 'eleve', etp: 12 }
};

// ═══════════════════════════════════════════════════════════════════════════
// DONNÉES PARCELLES & CULTURES DÉMO
// ═══════════════════════════════════════════════════════════════════════════

const PARCELLES_DEMO = [
  { id: 'A', nom: 'Parcelle A', surface: 50, texture: 'limono-argileux', ph: 6.5, profondeur: 40 },
  { id: 'B', nom: 'Parcelle B', surface: 50, texture: 'limoneux', ph: 6.8, profondeur: 35 },
  { id: 'C', nom: 'Parcelle C', surface: 50, texture: 'argilo-limoneux', ph: 6.3, profondeur: 45 },
  { id: 'D', nom: 'Parcelle D', surface: 50, texture: 'limono-sableux', ph: 6.7, profondeur: 30 },
  { id: 'E', nom: 'Parcelle E', surface: 50, texture: 'limoneux', ph: 7.0, profondeur: 35 },
];

const CULTURES_DEMO = {
  A: {
    nom: 'Chou cabus', icone: '🥬', famille: 'Brassicacées',
    phases: {
      semis: { debut: '02-15', fin: '03-15', lieu: 'Serre pépinière' },
      repiquage: { debut: '04-15', fin: '05-01' },
      croissance: { debut: '05-01', fin: '09-01' },
      recolte: { debut: '09-15', fin: '12-31' }
    },
    besoins: { temp_min_germination: 8, temp_optimale: 18, eau: 'eleve', ph_min: 6.0, ph_max: 7.5 },
    rendement: '3-5 kg/m²',
    conseils: {
      '01': 'Repos hivernal. Préparer les commandes de graines.',
      '02': 'Semis en pépinière à 15-18°C. Terreau léger.',
      '03': 'Éclaircir si nécessaire. Endurcir avant repiquage.',
      '04': 'Repiquage après les dernières gelées. Espacement 50x60cm.',
      '05': 'Arrosage régulier. Surveiller les altises.',
      '06': 'Binage, paillage. Attention limaces !',
      '07': 'Arrosage si sécheresse. Les pommes se forment.',
      '08': 'Surveiller piéride du chou. Traitement bio si besoin.',
      '09': 'Début récolte choux précoces.',
      '10': 'Récolte principale. Stocker au frais.',
      '11': 'Récolte continue. Supporter légères gelées.',
      '12': 'Fin de récolte. Arracher les trognons.'
    }
  },
  B: {
    nom: 'Courge Butternut', icone: '🎃', famille: 'Cucurbitacées',
    phases: {
      semis: { debut: '04-15', fin: '05-15', lieu: 'Godets sous abri' },
      repiquage: { debut: '05-20', fin: '06-15' },
      croissance: { debut: '06-15', fin: '09-01' },
      recolte: { debut: '09-15', fin: '11-15' }
    },
    besoins: { temp_min_germination: 15, temp_optimale: 22, eau: 'eleve', ph_min: 6.0, ph_max: 7.0 },
    rendement: '2-4 kg/m²',
    conseils: {
      '01': 'Repos. Planifier les cultures.',
      '02': 'Repos. Vérifier stocks de graines.',
      '03': 'Préparer le sol. Apport compost.',
      '04': 'Semis en godets, 2 graines par godet. 20-25°C.',
      '05': 'Plantation après Saints de Glace. Sol réchauffé.',
      '06': 'Paillage épais. Arrosage au pied.',
      '07': 'Floraison. Pollinisation par insectes.',
      '08': 'Fruits grossissent. Limiter à 4-5 fruits/pied.',
      '09': 'Maturation. Pédoncule liégeux = mûr.',
      '10': 'Récolte avant gelées. Laisser ressuyer.',
      '11': 'Fin récolte. Conservation 6 mois en cave.',
      '12': 'Stockage. Vérifier régulièrement.'
    }
  },
  C: {
    nom: 'Poireau Solaise', icone: '🧅', famille: 'Alliacées',
    phases: {
      semis: { debut: '02-15', fin: '03-31', lieu: 'Pleine terre / abri' },
      repiquage: { debut: '06-01', fin: '07-31' },
      croissance: { debut: '07-01', fin: '10-01' },
      recolte: { debut: '10-15', fin: '04-30' }
    },
    besoins: { temp_min_germination: 10, temp_optimale: 18, eau: 'moyen', ph_min: 6.0, ph_max: 7.0 },
    rendement: '2.5-4 kg/m²',
    conseils: {
      '01': 'Récolte possible même sous neige !',
      '02': 'Semis en pépinière. Terre fine, humide.',
      '03': 'Levée lente. Garder humide.',
      '04': 'Plants grandissent. Éclaircir.',
      '05': 'Préparer planches. Attendre plants crayon.',
      '06': 'Repiquage ! Habiller plants. Planter profond.',
      '07': 'Arrosage, binage. Premier buttage.',
      '08': 'Buttages successifs pour blanchir fût.',
      '09': 'Surveiller teigne. Buttage final.',
      '10': 'Début récolte au fur et à mesure.',
      '11': 'Récolte continue. Pailler si grand froid.',
      '12': 'Récolte hiver. Résiste au gel.'
    }
  },
  D: {
    nom: 'Carotte Nantaise', icone: '🥕', famille: 'Apiacées',
    phases: {
      semis: { debut: '03-15', fin: '07-15', lieu: 'Pleine terre' },
      croissance: { debut: '04-01', fin: '09-01' },
      recolte: { debut: '07-01', fin: '11-30' }
    },
    besoins: { temp_min_germination: 7, temp_optimale: 15, eau: 'moyen', ph_min: 6.0, ph_max: 7.0 },
    rendement: '3-6 kg/m²',
    conseils: {
      '01': 'Repos. Commander graines.',
      '02': 'Préparer sol. Ameublir en profondeur.',
      '03': 'Semis en place. Sol meuble, sans cailloux !',
      '04': 'Levée lente (15-20j). Patience !',
      '05': 'Éclaircir à 5cm. Butter légèrement.',
      '06': 'Binage, paillage. Associer avec poireaux.',
      '07': 'Début récolte primeurs.',
      '08': 'Récolte continue. Arrosage si sec.',
      '09': 'Récolte principale.',
      '10': 'Stocker en silo.',
      '11': 'Récolte avant grosses gelées.',
      '12': 'Stockage. Vérifier conservation.'
    }
  },
  E: {
    nom: 'JACHÈRE', icone: '🌿', famille: 'Repos du sol',
    phases: {
      semis: { debut: '09-01', fin: '10-15', lieu: 'Engrais vert' },
      croissance: { debut: '10-01', fin: '03-01' },
      destruction: { debut: '03-01', fin: '04-01' }
    },
    besoins: { temp_min_germination: 5, temp_optimale: 12, eau: 'faible', ph_min: 5.5, ph_max: 8.0 },
    rendement: 'Régénération sol',
    conseils: {
      '01': 'Engrais vert en place. Couvre et protège le sol.',
      '02': 'Prévoir destruction. Sol se régénère.',
      '03': 'Broyage/enfouissement engrais vert.',
      '04': 'Sol se régénère. Matière organique se décompose.',
      '05': 'Sol au repos. Analyse possible.',
      '06': 'Sol au repos. Observer la faune du sol.',
      '07': 'Analyse de sol recommandée.',
      '08': 'Préparer semis engrais vert.',
      '09': 'Semis engrais vert (moutarde, phacélie).',
      '10': 'Levée engrais vert.',
      '11': 'Protection hivernale du sol.',
      '12': 'Couverture du sol. Gel bénéfique.'
    }
  }
};

const MOIS = [
  { id: 1, nom: 'Janvier', court: 'Jan', icone: '❄️', saison: 'hiver' },
  { id: 2, nom: 'Février', court: 'Fév', icone: '❄️', saison: 'hiver' },
  { id: 3, nom: 'Mars', court: 'Mar', icone: '🌱', saison: 'printemps' },
  { id: 4, nom: 'Avril', court: 'Avr', icone: '🌸', saison: 'printemps' },
  { id: 5, nom: 'Mai', court: 'Mai', icone: '☀️', saison: 'printemps' },
  { id: 6, nom: 'Juin', court: 'Jun', icone: '☀️', saison: 'ete' },
  { id: 7, nom: 'Juillet', court: 'Jul', icone: '🌡️', saison: 'ete' },
  { id: 8, nom: 'Août', court: 'Aoû', icone: '🌡️', saison: 'ete' },
  { id: 9, nom: 'Septembre', court: 'Sep', icone: '🍂', saison: 'automne' },
  { id: 10, nom: 'Octobre', court: 'Oct', icone: '🍂', saison: 'automne' },
  { id: 11, nom: 'Novembre', court: 'Nov', icone: '🌧️', saison: 'automne' },
  { id: 12, nom: 'Décembre', court: 'Déc', icone: '❄️', saison: 'hiver' }
];

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

const parseDate = (mmdd) => {
  if (!mmdd) return null;
  const [mm, dd] = mmdd.split('-').map(Number);
  return { month: mm, day: dd };
};

const isInRange = (currentMonth, debut, fin) => {
  if (!debut) return false;
  const d = parseDate(debut);
  const f = fin ? parseDate(fin) : { month: 12, day: 31 };
  if (!d) return false;
  if (d.month <= f.month) {
    return currentMonth >= d.month && currentMonth <= f.month;
  } else {
    return currentMonth >= d.month || currentMonth <= f.month;
  }
};

const getPhaseActive = (culture, mois) => {
  for (const [phase, dates] of Object.entries(culture.phases)) {
    if (isInRange(mois, dates.debut, dates.fin)) return phase;
  }
  return 'repos';
};

const getPhaseColor = (phase) => {
  const colors = {
    semis: 'bg-emerald-100 text-emerald-800',
    repiquage: 'bg-green-100 text-green-800',
    croissance: 'bg-blue-100 text-blue-800',
    floraison: 'bg-pink-100 text-pink-800',
    maturation: 'bg-orange-100 text-orange-800',
    recolte: 'bg-amber-100 text-amber-800',
    destruction: 'bg-gray-100 text-gray-800',
    repos: 'bg-gray-50 text-gray-500'
  };
  return colors[phase] || colors.repos;
};

const getPhaseLabel = (phase) => {
  const labels = {
    semis: '🌱 Semis',
    repiquage: '🌿 Repiquage',
    croissance: '📈 Croissance',
    floraison: '🌸 Floraison',
    maturation: '🍅 Maturation',
    recolte: '🧺 Récolte',
    destruction: '🔄 Destruction',
    repos: '💤 Repos'
  };
  return labels[phase] || phase;
};

// Estimer humidité sol (fonction simplifiée côté client)
const estimerHumiditeSol = (meteo, texture) => {
  const coefTexture = {
    'argileux': 1.3, 'argilo-limoneux': 1.2, 'limoneux': 1.0,
    'limono-argileux': 1.1, 'limono-sableux': 0.8, 'sableux': 0.6
  };
  const coef = coefTexture[texture] || 1.0;
  const bilan = meteo.precip - meteo.etp;
  
  let humidite = 35 * coef; // Base capacité au champ
  if (bilan > 20) humidite += 15;
  else if (bilan > 0) humidite += 5;
  else if (bilan > -20) humidite -= 5;
  else humidite -= 15;
  
  return Math.max(10, Math.min(60, Math.round(humidite)));
};

// Évaluer conditions pour une culture
const evaluerConditions = (culture, parcelle, meteo) => {
  const besoins = culture.besoins;
  const alerts = [];
  let score = 100;
  
  // Température sol
  if (meteo.temp_sol < besoins.temp_min_germination) {
    alerts.push({ type: 'danger', msg: `Sol trop froid (${meteo.temp_sol}°C < ${besoins.temp_min_germination}°C)` });
    score -= 30;
  } else if (meteo.temp_sol < besoins.temp_optimale - 5) {
    alerts.push({ type: 'warning', msg: `Sol frais (${meteo.temp_sol}°C)` });
    score -= 10;
  }
  
  // Risque gel
  if (meteo.risque_gel === 'eleve' || meteo.risque_gel === 'tres_eleve') {
    alerts.push({ type: 'danger', msg: `Risque de gel élevé (${meteo.jours_gel} jours)` });
    score -= 25;
  } else if (meteo.risque_gel === 'moyen') {
    alerts.push({ type: 'warning', msg: 'Risque de gel modéré' });
    score -= 10;
  }
  
  // pH sol
  if (parcelle.ph < besoins.ph_min) {
    alerts.push({ type: 'warning', msg: `Sol trop acide (pH ${parcelle.ph} < ${besoins.ph_min})` });
    score -= 15;
  } else if (parcelle.ph > besoins.ph_max) {
    alerts.push({ type: 'warning', msg: `Sol trop alcalin (pH ${parcelle.ph} > ${besoins.ph_max})` });
    score -= 15;
  }
  
  // Humidité estimée
  const humidite = estimerHumiditeSol(meteo, parcelle.texture);
  if (humidite < 20) {
    alerts.push({ type: 'warning', msg: `Sol sec (${humidite}%)` });
    score -= 15;
  } else if (humidite > 55) {
    alerts.push({ type: 'warning', msg: `Sol très humide (${humidite}%)` });
    score -= 10;
  }
  
  return { score: Math.max(0, score), alerts, humidite };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Jauge météo
// ═══════════════════════════════════════════════════════════════════════════

const MeteoGauge = ({ label, value, unit, min, max, optimal, icon: Icon, color }) => {
  const pct = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
  const optimalPct = optimal ? ((optimal - min) / (max - min)) * 100 : null;
  
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
        {Icon && <Icon className="h-3 w-3" />}
        <span>{label}</span>
      </div>
      <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full ${color}`}
          style={{ width: `${pct}%` }}
        />
        {optimalPct && (
          <div 
            className="absolute top-0 h-full w-0.5 bg-green-600"
            style={{ left: `${optimalPct}%` }}
            title={`Optimal: ${optimal}${unit}`}
          />
        )}
      </div>
      <div className="text-right text-xs font-medium mt-0.5">{value}{unit}</div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Carte vecteur météo
// ═══════════════════════════════════════════════════════════════════════════

const VecteurMeteoCard = ({ meteo, mois }) => {
  return (
    <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-4 shadow-lg border border-sky-200">
      <h3 className="font-semibold text-sky-800 mb-3 flex items-center gap-2">
        <Cloud className="h-5 w-5" />
        Vecteur Météo - {MOIS[mois - 1]?.nom}
      </h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Températures */}
        <div className="space-y-2">
          <MeteoGauge 
            label="T° Air moy" 
            value={meteo.temp_moy} 
            unit="°C" 
            min={-5} max={35} 
            optimal={18}
            icon={Thermometer}
            color="bg-gradient-to-r from-blue-400 to-red-400"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Min: {meteo.temp_min}°C</span>
            <span>Max: {meteo.temp_max}°C</span>
          </div>
        </div>
        
        {/* T° Sol */}
        <div className="space-y-2">
          <MeteoGauge 
            label="T° Sol 10cm" 
            value={meteo.temp_sol} 
            unit="°C" 
            min={0} max={30}
            optimal={15}
            icon={Activity}
            color="bg-gradient-to-r from-amber-400 to-orange-500"
          />
          <div className="text-xs text-gray-500">
            {meteo.temp_sol >= 15 ? '✅ Semis possible' : 
             meteo.temp_sol >= 10 ? '⚠️ Semis limité' : '❌ Trop froid'}
          </div>
        </div>
        
        {/* Précipitations */}
        <div className="space-y-2">
          <MeteoGauge 
            label="Précipitations" 
            value={meteo.precip} 
            unit="mm" 
            min={0} max={120}
            icon={CloudRain}
            color="bg-gradient-to-r from-sky-400 to-blue-500"
          />
          <div className="text-xs text-gray-500">
            ETP: {meteo.etp}mm • Bilan: {meteo.precip - meteo.etp > 0 ? '+' : ''}{meteo.precip - meteo.etp}mm
          </div>
        </div>
        
        {/* Humidité air */}
        <div className="space-y-2">
          <MeteoGauge 
            label="Humidité air" 
            value={meteo.humidite} 
            unit="%" 
            min={40} max={100}
            optimal={70}
            icon={Droplets}
            color="bg-gradient-to-r from-cyan-400 to-teal-500"
          />
          <div className="text-xs text-gray-500">
            Ensoleillement: {meteo.ensoleillement}h
          </div>
        </div>
      </div>
      
      {/* Alertes */}
      <div className="mt-3 flex flex-wrap gap-2">
        <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
          meteo.risque_gel === 'nul' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          <Snowflake className="h-3 w-3" />
          Gel: {meteo.risque_gel} ({meteo.jours_gel}j)
        </span>
        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 flex items-center gap-1">
          <Wind className="h-3 w-3" />
          Vent: {meteo.vent} km/h
        </span>
        <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 flex items-center gap-1">
          <Sun className="h-3 w-3" />
          {meteo.ensoleillement}h soleil
        </span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Carte vecteur sol
// ═══════════════════════════════════════════════════════════════════════════

const VecteurSolCard = ({ parcelle, meteo }) => {
  const humidite = estimerHumiditeSol(meteo, parcelle.texture);
  
  const getHumiditeStatus = (h) => {
    if (h < 20) return { label: 'Sec', color: 'text-orange-600', bg: 'bg-orange-100' };
    if (h < 30) return { label: 'Frais', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (h < 50) return { label: 'Optimal', color: 'text-green-600', bg: 'bg-green-100' };
    return { label: 'Humide', color: 'text-blue-600', bg: 'bg-blue-100' };
  };
  
  const status = getHumiditeStatus(humidite);
  
  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 shadow-lg border border-amber-200">
      <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
        <Leaf className="h-5 w-5" />
        Vecteur Sol - {parcelle.nom}
      </h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Humidité sol estimée */}
        <div className="space-y-2">
          <MeteoGauge 
            label="Humidité sol" 
            value={humidite} 
            unit="%" 
            min={10} max={60}
            optimal={40}
            icon={Droplets}
            color="bg-gradient-to-r from-amber-400 to-yellow-500"
          />
          <div className={`text-xs px-2 py-0.5 rounded-full text-center ${status.bg} ${status.color}`}>
            {status.label}
          </div>
        </div>
        
        {/* pH */}
        <div className="space-y-2">
          <MeteoGauge 
            label="pH sol" 
            value={parcelle.ph} 
            unit="" 
            min={5} max={8}
            optimal={6.5}
            icon={Activity}
            color="bg-gradient-to-r from-purple-400 to-indigo-500"
          />
          <div className="text-xs text-gray-500">
            {parcelle.ph >= 6.0 && parcelle.ph <= 7.0 ? '✅ Idéal maraîchage' : '⚠️ Ajuster'}
          </div>
        </div>
        
        {/* Texture */}
        <div className="space-y-1">
          <div className="text-xs text-gray-600 flex items-center gap-1">
            <Info className="h-3 w-3" /> Texture
          </div>
          <div className="text-sm font-medium capitalize">{parcelle.texture}</div>
          <div className="text-xs text-gray-500">Prof: {parcelle.profondeur}cm</div>
        </div>
        
        {/* Travaillabilité */}
        <div className="space-y-1">
          <div className="text-xs text-gray-600">Travaillabilité</div>
          <div className={`text-sm font-medium ${
            humidite > 20 && humidite < 50 ? 'text-green-600' : 'text-orange-600'
          }`}>
            {humidite > 50 ? '❌ Sol trop mouillé' :
             humidite < 20 ? '⚠️ Sol très sec' :
             '✅ Sol travaillable'}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Score de favorabilité
// ═══════════════════════════════════════════════════════════════════════════

const FavorabiliteScore = ({ culture, parcelle, meteo }) => {
  const { score, alerts } = evaluerConditions(culture, parcelle, meteo);
  
  const getScoreColor = (s) => {
    if (s >= 80) return 'text-green-600 bg-green-100';
    if (s >= 60) return 'text-yellow-600 bg-yellow-100';
    if (s >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };
  
  const getScoreLabel = (s) => {
    if (s >= 80) return 'Optimal';
    if (s >= 60) return 'Favorable';
    if (s >= 40) return 'Limite';
    return 'Défavorable';
  };
  
  return (
    <div className="bg-white rounded-lg p-3 border">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">Favorabilité</span>
        <span className={`text-lg font-bold px-2 py-0.5 rounded ${getScoreColor(score)}`}>
          {score}% - {getScoreLabel(score)}
        </span>
      </div>
      
      {alerts.length > 0 && (
        <div className="space-y-1">
          {alerts.map((alert, idx) => (
            <div key={idx} className={`text-xs flex items-center gap-1 ${
              alert.type === 'danger' ? 'text-red-600' : 'text-orange-600'
            }`}>
              <AlertCircle className="h-3 w-3" />
              {alert.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const SimulateurCulturalV3 = () => {
  const [moisActuel, setMoisActuel] = useState(new Date().getMonth() + 1);
  const [selectedParcelle, setSelectedParcelle] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVecteurs, setShowVecteurs] = useState(true);
  
  // Auto-play
  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setMoisActuel(m => m >= 12 ? 1 : m + 1);
    }, 2000);
    return () => clearInterval(timer);
  }, [isPlaying]);

  const moisInfo = MOIS[moisActuel - 1];
  const meteoMois = NORMALES_METEO[moisActuel];
  
  // État des parcelles
  const etatParcelles = useMemo(() => {
    return PARCELLES_DEMO.map(parcelle => {
      const culture = CULTURES_DEMO[parcelle.id];
      const phase = getPhaseActive(culture, moisActuel);
      const conseil = culture.conseils[String(moisActuel).padStart(2, '0')] || 'Pas de conseil spécifique.';
      const evaluation = evaluerConditions(culture, parcelle, meteoMois);
      
      return {
        ...parcelle,
        culture,
        phase,
        conseil,
        evaluation
      };
    });
  }, [moisActuel]);

  const parcelleSelectionnee = selectedParcelle 
    ? etatParcelles.find(p => p.id === selectedParcelle)
    : null;

  return (
    <div className="space-y-6 p-4">
      {/* HEADER */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sprout className="h-8 w-8" />
              Simulateur Cultural V3
            </h1>
            <p className="text-green-100 mt-1">Timeline avec Vecteurs Météo & Sol - Mode Démo</p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowVecteurs(!showVecteurs)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                showVecteurs ? 'bg-white text-green-700' : 'bg-green-700 text-white'
              }`}
            >
              {showVecteurs ? '📊 Vecteurs ON' : '📊 Vecteurs OFF'}
            </button>
          </div>
        </div>
      </div>

      {/* CONTRÔLES TIMELINE */}
      <div className="bg-white rounded-xl p-4 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMoisActuel(m => m <= 1 ? 12 : m - 1)}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            
            <div className="text-center">
              <div className="text-4xl">{moisInfo.icone}</div>
              <div className="text-xl font-bold text-gray-800">{moisInfo.nom}</div>
              <div className="text-sm text-gray-500 capitalize">{moisInfo.saison}</div>
            </div>
            
            <button
              onClick={() => setMoisActuel(m => m >= 12 ? 1 : m + 1)}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
                isPlaying ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {isPlaying ? 'Pause' : 'Play'}
            </button>
            <button
              onClick={() => setMoisActuel(new Date().getMonth() + 1)}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              title="Mois actuel"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        {/* Barre de progression */}
        <div className="flex gap-1">
          {MOIS.map(m => (
            <button
              key={m.id}
              onClick={() => setMoisActuel(m.id)}
              className={`flex-1 h-8 rounded text-xs font-medium transition-all ${
                m.id === moisActuel 
                  ? 'bg-green-500 text-white scale-110 shadow-lg z-10' 
                  : m.saison === 'hiver' ? 'bg-blue-100 hover:bg-blue-200'
                  : m.saison === 'printemps' ? 'bg-green-100 hover:bg-green-200'
                  : m.saison === 'ete' ? 'bg-yellow-100 hover:bg-yellow-200'
                  : 'bg-orange-100 hover:bg-orange-200'
              }`}
            >
              {m.court}
            </button>
          ))}
        </div>
      </div>

      {/* VECTEURS MÉTÉO & SOL */}
      {showVecteurs && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <VecteurMeteoCard meteo={meteoMois} mois={moisActuel} />
          {parcelleSelectionnee ? (
            <VecteurSolCard parcelle={parcelleSelectionnee} meteo={meteoMois} />
          ) : (
            <div className="bg-gray-50 rounded-xl p-4 shadow-lg border border-gray-200 flex items-center justify-center">
              <p className="text-gray-500">👆 Sélectionnez une parcelle pour voir son profil sol</p>
            </div>
          )}
        </div>
      )}

      {/* PARCELLES */}
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Leaf className="h-5 w-5 text-green-600" />
          État des parcelles
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {etatParcelles.map(parcelle => (
            <div
              key={parcelle.id}
              onClick={() => setSelectedParcelle(selectedParcelle === parcelle.id ? null : parcelle.id)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
                selectedParcelle === parcelle.id ? 'border-green-500 shadow-lg' : 'border-gray-200'
              }`}
            >
              <div className="text-4xl text-center mb-2">{parcelle.culture.icone}</div>
              <div className="text-center font-semibold text-gray-800">{parcelle.nom}</div>
              <div className="text-center text-sm text-gray-600 mb-2">{parcelle.culture.nom}</div>
              <div className={`text-center text-xs px-2 py-1 rounded-full ${getPhaseColor(parcelle.phase)}`}>
                {getPhaseLabel(parcelle.phase)}
              </div>
              
              {/* Score favorabilité */}
              <div className={`mt-2 text-center text-xs font-medium px-2 py-1 rounded-full ${
                parcelle.evaluation.score >= 80 ? 'bg-green-100 text-green-700' :
                parcelle.evaluation.score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                parcelle.evaluation.score >= 40 ? 'bg-orange-100 text-orange-700' :
                'bg-red-100 text-red-700'
              }`}>
                {parcelle.evaluation.score}% favorable
              </div>
              
              <div className="text-center text-xs text-gray-400 mt-2">{parcelle.surface} m²</div>
            </div>
          ))}
        </div>
      </div>

      {/* DÉTAIL PARCELLE */}
      {parcelleSelectionnee && (
        <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span className="text-2xl">{parcelleSelectionnee.culture.icone}</span>
              {parcelleSelectionnee.nom} - {parcelleSelectionnee.culture.nom}
            </h2>
            <button onClick={() => setSelectedParcelle(null)} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Infos culture */}
            <div className="space-y-2 text-sm">
              <div><span className="font-medium text-gray-600">Famille :</span> {parcelleSelectionnee.culture.famille}</div>
              <div><span className="font-medium text-gray-600">Rendement :</span> {parcelleSelectionnee.culture.rendement}</div>
              <div><span className="font-medium text-gray-600">Phase :</span> <span className={`px-2 py-0.5 rounded-full text-xs ${getPhaseColor(parcelleSelectionnee.phase)}`}>{getPhaseLabel(parcelleSelectionnee.phase)}</span></div>
              <div><span className="font-medium text-gray-600">Texture sol :</span> {parcelleSelectionnee.texture}</div>
              <div><span className="font-medium text-gray-600">pH :</span> {parcelleSelectionnee.ph}</div>
            </div>
            
            {/* Score favorabilité */}
            <FavorabiliteScore 
              culture={parcelleSelectionnee.culture}
              parcelle={parcelleSelectionnee}
              meteo={meteoMois}
            />
            
            {/* Conseil du mois */}
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-green-600 flex-shrink-0" />
                <div>
                  <div className="font-medium text-green-800 mb-1">Conseil {moisInfo.nom}</div>
                  <div className="text-sm text-green-700">{parcelleSelectionnee.conseil}</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Phases */}
          <div className="mt-4">
            <h3 className="font-medium text-gray-700 mb-2">Cycle cultural :</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {Object.entries(parcelleSelectionnee.culture.phases).map(([phase, dates], idx, arr) => (
                <React.Fragment key={phase}>
                  <div className={`px-3 py-1 rounded-lg text-sm ${parcelleSelectionnee.phase === phase ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    {getPhaseLabel(phase)}
                    <div className="text-xs opacity-75">{dates.debut} → {dates.fin || '...'}</div>
                  </div>
                  {idx < arr.length - 1 && <ArrowRight className="h-4 w-4 text-gray-400" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* GANTT */}
      <div className="bg-white rounded-xl p-6 shadow-lg overflow-x-auto">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-600" />
          Vue annuelle
        </h2>
        <div className="min-w-[700px]">
          <div className="flex border-b border-gray-200 mb-2">
            <div className="w-28 flex-shrink-0 font-medium text-gray-600 text-sm p-2">Parcelle</div>
            {MOIS.map(m => (
              <div key={m.id} className={`flex-1 text-center text-xs p-2 ${m.id === moisActuel ? 'bg-green-100 font-bold' : 'text-gray-500'}`}>
                {m.court}
              </div>
            ))}
          </div>
          {PARCELLES_DEMO.map(parcelle => {
            const culture = CULTURES_DEMO[parcelle.id];
            return (
              <div key={parcelle.id} className="flex items-center border-b border-gray-100">
                <div className="w-28 flex-shrink-0 p-2 flex items-center gap-2">
                  <span>{culture.icone}</span>
                  <span className="text-sm font-medium">{parcelle.id}</span>
                </div>
                {MOIS.map(m => {
                  const phase = getPhaseActive(culture, m.id);
                  return (
                    <div key={m.id} className={`flex-1 h-8 ${m.id === moisActuel ? 'ring-2 ring-green-500 ring-inset' : ''}`}>
                      {phase !== 'repos' && (
                        <div className={`w-full h-6 rounded ${
                          phase === 'semis' ? 'bg-emerald-400' :
                          phase === 'repiquage' ? 'bg-green-400' :
                          phase === 'croissance' ? 'bg-blue-400' :
                          phase === 'recolte' ? 'bg-amber-400' :
                          phase === 'destruction' ? 'bg-gray-400' : 'bg-gray-300'
                        }`} title={`${culture.nom} - ${phase}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div className="flex justify-center gap-4 mt-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-emerald-400 rounded"></span> Semis</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-green-400 rounded"></span> Repiquage</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-blue-400 rounded"></span> Croissance</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-amber-400 rounded"></span> Récolte</span>
          </div>
        </div>
      </div>

      {/* ROTATION 5 ANS */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 shadow-lg border border-purple-200">
        <h2 className="text-lg font-semibold text-purple-800 mb-4 flex items-center gap-2">
          <RotateCcw className="h-5 w-5" />
          Plan de rotation 5 ans
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-purple-600">
                <th className="text-left p-2">Parcelle</th>
                <th className="text-center p-2">2024</th>
                <th className="text-center p-2 bg-purple-100 rounded">2025</th>
                <th className="text-center p-2">2026</th>
                <th className="text-center p-2">2027</th>
                <th className="text-center p-2">2028</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['A', '🎃 Courges', '🥬 Choux', '🧅 Poireaux', '🥕 Carottes', '🌿 Jachère'],
                ['B', '🌿 Jachère', '🎃 Courges', '🥬 Choux', '🧅 Poireaux', '🥕 Carottes'],
                ['C', '🥬 Choux', '🧅 Poireaux', '🥕 Carottes', '🌿 Jachère', '🎃 Courges'],
                ['D', '🧅 Poireaux', '🥕 Carottes', '🌿 Jachère', '🎃 Courges', '🥬 Choux'],
                ['E', '🥕 Carottes', '🌿 Jachère', '🎃 Courges', '🥬 Choux', '🧅 Poireaux'],
              ].map(row => (
                <tr key={row[0]} className="border-t border-purple-200">
                  <td className="p-2 font-medium">Parcelle {row[0]}</td>
                  <td className="p-2 text-center">{row[1]}</td>
                  <td className="p-2 text-center bg-purple-100 font-medium">{row[2]}</td>
                  <td className="p-2 text-center">{row[3]}</td>
                  <td className="p-2 text-center">{row[4]}</td>
                  <td className="p-2 text-center">{row[5]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 p-3 bg-white rounded-lg text-sm text-purple-700">
          <strong>💡 Principe bio :</strong> Rotation obligatoire par famille botanique. La terre n'est pas de la ouate mouillée !
        </div>
      </div>

      {/* LÉGENDE VECTEURS */}
      <div className="bg-gray-50 rounded-xl p-4 shadow">
        <h3 className="font-medium text-gray-700 mb-3">📊 Légende des vecteurs</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="font-medium text-sky-700">🌡️ Vecteur Météo</div>
            <ul className="text-gray-600 text-xs mt-1 space-y-0.5">
              <li>• T° air (min/moy/max)</li>
              <li>• T° sol à 10cm</li>
              <li>• Précipitations & ETP</li>
              <li>• Humidité air, vent</li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-amber-700">🌱 Vecteur Sol</div>
            <ul className="text-gray-600 text-xs mt-1 space-y-0.5">
              <li>• Humidité sol (estimée)</li>
              <li>• pH</li>
              <li>• Texture</li>
              <li>• Travaillabilité</li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-green-700">📈 Favorabilité</div>
            <ul className="text-gray-600 text-xs mt-1 space-y-0.5">
              <li>• 80%+ = Optimal</li>
              <li>• 60-79% = Favorable</li>
              <li>• 40-59% = Limite</li>
              <li>• &lt;40% = Défavorable</li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-red-700">⚠️ Alertes</div>
            <ul className="text-gray-600 text-xs mt-1 space-y-0.5">
              <li>• Risque gel</li>
              <li>• Sol trop froid</li>
              <li>• pH hors plage</li>
              <li>• Stress hydrique</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="text-center text-sm text-gray-500 py-4">
        🌱 Simulateur Cultural V3 - Farm-Booking - Papy 73+ - Joshua Edition 🎮📡
        <br />
        <span className="text-xs">"The only winning move is to PREDICT"</span>
      </div>
    </div>
  );
};

export default SimulateurCulturalV3;